import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, ChevronDown } from "lucide-react";
import FadeIn from "./FadeIn";
import { useLang } from "@/i18n/LanguageContext";
import { COUNTRY_CODES } from "@/i18n/countries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
const USERNAME_RE = /^[a-z0-9_]{3,24}$/;

const Field = ({ label, optional, optionalLabel, error, children }) => (
  <div>
    <label className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
      {label}
      {optional && (
        <span className="rounded-full border border-white/10 px-2 py-0.5 text-[9px] normal-case tracking-normal text-ink-subtle">
          {optionalLabel}
        </span>
      )}
    </label>
    {children}
    {error && <p className="mt-2 text-xs text-tactical" data-testid="form-field-error">{error}</p>}
  </div>
);

export const RegisterForm = () => {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [values, setValues] = useState(() => {
    let ref = "";
    let country = "";
    try {
      ref = (new URLSearchParams(window.location.search).get("ref") || "").trim().toLowerCase();
      if (ref && USERNAME_RE.test(ref)) {
        localStorage.setItem("prenet-ref", ref);
      } else {
        ref = localStorage.getItem("prenet-ref") || "";
      }
      // Auto-detect from the browser locale (e.g. fr-FR -> FR)
      const region = ((navigator.language || "").split("-")[1] || "").toUpperCase();
      if (COUNTRY_CODES.includes(region)) country = region;
    } catch {}
    return { name: "", email: "", username: "", country, sponsor: USERNAME_RE.test(ref) ? ref : "" };
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  // Country names rendered by the browser in the current site language
  const countryOptions = useMemo(() => {
    let dn;
    try {
      dn = new Intl.DisplayNames([lang], { type: "region" });
    } catch {
      return [];
    }
    return COUNTRY_CODES.map((code) => ({ code, name: dn.of(code) || code }))
      .sort((a, b) => a.name.localeCompare(b.name, lang));
  }, [lang]);

  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined, generic: undefined }));
  };

  const validate = () => {
    const er = {};
    if (values.name.trim().length < 2) er.name = t.form.errName;
    if (!EMAIL_RE.test(values.email.trim())) er.email = t.form.errEmail;
    if (!USERNAME_RE.test(values.username.trim().toLowerCase())) er.username = t.form.errUsername;
    if (!values.country) er.country = t.form.errCountry;
    const sp = values.sponsor.trim().toLowerCase();
    if (sp && !USERNAME_RE.test(sp)) er.sponsor = t.form.errSponsor;
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch(`${API}/registrations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, lang }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = typeof data.detail === "string" ? data.detail : "";
        const msg422 = Array.isArray(data.detail)
          ? data.detail.map((d) => (d && d.msg) || "").join(" ")
          : "";
        const er2 = {};
        if (detail === "EMAIL_TAKEN" || msg422.includes("INVALID_EMAIL")) er2.email = detail === "EMAIL_TAKEN" ? t.form.errEmailTaken : t.form.errEmail;
        if (detail === "USERNAME_TAKEN") er2.username = t.form.errUsernameTaken;
        if (msg422.includes("INVALID_NAME")) er2.name = t.form.errName;
        if (msg422.includes("INVALID_USERNAME")) er2.username = t.form.errUsername;
        if (msg422.includes("INVALID_SPONSOR")) er2.sponsor = t.form.errSponsor;
        if (msg422.includes("INVALID_COUNTRY")) er2.country = t.form.errCountry;
        if (!Object.keys(er2).length) er2.generic = t.form.errGeneric;
        setErrors(er2);
        setStatus("idle");
        return;
      }
      navigate(`/welcome/${data.username}`);
    } catch {
      setErrors({ generic: t.form.errGeneric });
      setStatus("idle");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-night-2/80 px-4 py-3.5 text-sm text-ink placeholder:text-ink-subtle outline-none transition-all focus:border-signal/60 focus:ring-1 focus:ring-signal/40";



  return (
    <section id="register" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-signal/40 to-transparent" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-signal">
              {t.form.eyebrow}
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold leading-tight text-ink sm:text-4xl lg:text-5xl">
              {t.form.title}
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink-muted">
              {t.form.sub}
            </p>
            <div className="mt-10 flex items-center gap-3 rounded-xl border border-signal/20 bg-night-2/60 px-5 py-4">
              <ShieldCheck size={20} className="shrink-0 text-signal2" strokeWidth={1.6} />
              <p className="text-sm text-ink-muted">{t.form.note}</p>
            </div>
          </FadeIn>

          <FadeIn delay={0.12}>
            {values.sponsor && (
              <div
                className="mb-2 inline-flex items-center gap-2 rounded-full border border-signal2/40 bg-signal2/10 px-4 py-2"
                data-testid="form-invited-chip"
              >
                <span className="h-2 w-2 rounded-full bg-signal2" />
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-signal2">
                  {t.form.invitedBy} @{values.sponsor}
                </span>
              </div>
            )}
                        <div className="mb-4 inline-flex items-center gap-2.5 rounded-full border border-tactical/40 bg-tactical/10 px-4 py-2">
              <span className="h-2 w-2 rounded-full bg-tactical animate-pulse-soft" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-tactical">
                {t.hero.eyebrow}
              </span>
            </div>
            <div className="glass rounded-3xl p-7 sm:p-9" data-testid="register-form-panel">
              <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={submit}
                    noValidate
                    className="space-y-5"
                    data-testid="register-form"
                  >
                    <Field label={t.form.name} error={errors.name}>
                      <input
                        type="text"
                        value={values.name}
                        onChange={set("name")}
                        placeholder={t.form.phName}
                        data-testid="form-name-input"
                        className={inputCls}
                      />
                    </Field>
                    <Field label={t.form.email} error={errors.email}>
                      <input
                        type="email"
                        value={values.email}
                        onChange={set("email")}
                        placeholder={t.form.phEmail}
                        data-testid="form-email-input"
                        className={inputCls}
                      />
                    </Field>
                    <Field label={t.form.username} error={errors.username}>
                      <input
                        type="text"
                        value={values.username}
                        onChange={set("username")}
                        placeholder={t.form.phUsername}
                        data-testid="form-username-input"
                        className={inputCls}
                      />
                    </Field>
                    <Field label={t.form.country} error={errors.country}>
                      <div className="relative">
                        <select
                          value={values.country}
                          onChange={set("country")}
                          data-testid="form-country-select"
                          className={`${inputCls} appearance-none pr-10 ${values.country ? "" : "text-ink-subtle"}`}
                        >
                          <option value="">{t.form.phCountry}</option>
                          {countryOptions.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          size={16}
                          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-ink-subtle"
                        />
                      </div>
                    </Field>
                    <Field
                      label={t.form.sponsor}
                      optional
                      optionalLabel={t.form.sponsorOptional}
                      error={errors.sponsor}
                    >
                      <input
                        type="text"
                        value={values.sponsor}
                        onChange={set("sponsor")}
                        placeholder={t.form.phSponsor}
                        data-testid="form-sponsor-input"
                        className={inputCls}
                      />
                    </Field>

                    {errors.generic && (
                      <p className="rounded-lg border border-tactical/40 bg-tactical/10 px-4 py-3 text-xs text-tactical" data-testid="form-generic-error">
                        {errors.generic}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      data-testid="form-submit-button"
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-tactical py-4 text-sm font-bold text-night transition-all hover:glow-tactical disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {status === "submitting" ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          {t.form.submitting}
                        </>
                      ) : (
                        t.form.submit
                      )}
                    </button>
              </motion.form>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default RegisterForm;
